import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { map, take, takeUntil } from 'rxjs/operators';
import { CallLanguageCodeDto } from 'src/core/models/call/call-language-code.dto';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { CallAgentChannel } from 'src/core/models/generic-lookup-type/call/call-agent-channel.glt';
import { GenericLookupDto } from 'src/core/models/generic-lookup/generic-lookup.dto';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { GenericLookupTypeState } from 'src/core/states/generic-lookup-type/generic-lookup-type.state';
import { IntegerValidator } from 'src/core/validators/shared/integer.validator';
import { GlobalSettingsService } from 'src/core/services/settings/global-settings.service';
import { DateFormatConstants } from 'src/core/constants/dateformat.constant';
import { TimeZoneConstants } from 'src/core/constants/timezone.constant';
import { FeatureConstants } from 'src/core/constants/feature-constant';
import { FeatureService } from 'src/core/services/feature/feature.service';
import { KeyValue } from '@angular/common';
import { ObjectHelper } from 'src/core/helpers/object.helper';
import { RoleHelper } from 'src/core/helpers/role-helper';
import { HasSubscription } from 'src/ca-shared/ca-shared.module';
import { AutoUnsubscribe } from 'src/core/decorators/auto-unsubscribe.decorator';
import { TimeZoneHelper } from 'src/core/services/helper/timezone-helper';
import { Operators } from 'src/core/models/request/operator.enum';

@Component({
  selector: 'ca-global-settings',
  templateUrl: './global-settings.component.html',
  styleUrls: ['./global-settings.component.scss'],
  standalone: false,
})
@AutoUnsubscribe()
export class GlobalSettingsComponent extends HasSubscription implements OnInit {
  settingsForm: FormGroup;
  globalSettingDtos: ConfigurationSettingsDto[] = [];
  channelTypes: GenericLookupDto[];
  systemStartDate: any;
  mixedLanguageType: string = 'Conversation.MixedLanguageType.SystemLanguage';
  systemLangCode: string;
  systemLangCodeTemp: string;
  otherLangCodes: string[];
  langCodesSettingValue: string[] = [];
  langCodesTemp: string[] = [];
  systemTimeZone: string;
  isSystemTimeZoneLocked: boolean;
  isSystemTimeZonePendingConfirmation: boolean;
  systemDateFormat: string;
  inboundAgentChannel: string;
  outboundAgentChannel: string;
  domainName: string;
  ldapCertificateName: string;
  maximumAnalysisTime: number;
  languages: CallLanguageCodeDto[] = [
    {
      id: 0,
      name: 'dummy',
      code: 'dummy',
      isVisible: false,
      disabled: false,
      isDeleted: false,
    },
  ];
  mixedLanguages: CallLanguageCodeDto[] = [];
  otherLanguagesDataSource: CallLanguageCodeDto[] = [];
  timeZones: any;
  userFeedbackReceiverEmail: string;
  userFeedbackFeatureEnabled = false;
  analyticsFeatureEnabled = false;
  languageDetectionFeatureEnabled = false;
  idleTimeoutEnabled = false;
  idleTimeoutMinute: number;
  dateFormats: string[];
  testEmailAddress: string;
  autoPlayEnabled = true;
  corsOriginForEmbeddedAccess: string;
  isSuperAdminUser: boolean = false;
  originalSystemLangCode: string;
  fullTimeEquivalent: number;
  isChangeExternalIdEnabled = true;
  quickSearchDateRange: number;
  skipLdapCertificateValidation = false;
  whiteLabelingEnabled: boolean = false;

  get getCurrentSystemLanguage() {
    return this.languages?.find(x => x.code == this.systemLangCode)?.name;
  }

  originalOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    return 0;
  };

  constructor(
    private globalSettingsService: GlobalSettingsService,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService,
    private store: Store,
    private config: ConfigStateService,
    private featureService: FeatureService,
    private roleHelper: RoleHelper,
    private fb: FormBuilder,
    private timeZoneHelper: TimeZoneHelper,
    private operators: Operators,
  ) {
    super();

    this.whiteLabelingEnabled =
      this.config.getSetting('WhiteLabeling:Enabled')?.toLowerCase() === 'true';
    this.userFeedbackFeatureEnabled = this.featureService.isEnabled(FeatureConstants.UserFeedback);
    this.analyticsFeatureEnabled = this.featureService.isEnabled(FeatureConstants.Analytics);
    this.languageDetectionFeatureEnabled = this.featureService.isEnabled(
      FeatureConstants.LanguageDetection,
    );
    // systemStartDate is a real UTC instant (tenant-zone midnight), so its calendar date is read
    // in the tenant zone rather than the browser zone. M/D/YYYY matches toLocaleDateString('en-US').
    const startDate = this.timeZoneHelper.GetZonedDateComponents(
      this.globalSettingsService.systemStartDate,
      this.globalSettingsService.systemTimeZone,
    );
    this.systemStartDate = `${startDate.month}/${startDate.day}/${startDate.year}`;
    this.mixedLanguageType = this.globalSettingsService.mixedLanguageType;
    this.systemLangCode = this.globalSettingsService.systemLanguageCode;
    this.originalSystemLangCode = this.globalSettingsService.systemLanguageCode;
    this.otherLangCodes = this.globalSettingsService.otherLangCodes
      .split(';')
      .filter(x => x.length > 0);
    this.systemTimeZone = TimeZoneConstants.normalizeTimeZoneId(
      this.globalSettingsService.systemTimeZone,
    );
    this.isSystemTimeZoneLocked =
      this.globalSettingsService.isSystemTimeZoneConfirmed &&
      this.globalSettingsService.hasEverHadConversationData &&
      !this.canBypassSystemTimeZoneLock();
    this.isSystemTimeZonePendingConfirmation = !this.globalSettingsService.isSystemTimeZoneConfirmed;
    this.systemDateFormat = this.globalSettingsService.systemDateFormat.replace(/M/g, 'm');
    this.inboundAgentChannel = this.globalSettingsService.inboundAgentChannel;
    this.outboundAgentChannel = this.globalSettingsService.outboundAgentChannel;
    this.domainName = this.globalSettingsService.domainName;
    this.ldapCertificateName = this.globalSettingsService.ldapCertificateName;
    this.userFeedbackReceiverEmail = this.globalSettingsService.userFeedbackReceiverEmail;
    this.maximumAnalysisTime = this.globalSettingsService.maximumAnalysisTime;
    this.idleTimeoutEnabled = this.globalSettingsService.idleTimeoutEnabled;
    this.idleTimeoutMinute = this.globalSettingsService.idleTimeoutMinute;
    this.autoPlayEnabled = this.globalSettingsService.autoPlayEnabled;
    this.corsOriginForEmbeddedAccess = this.globalSettingsService.corsOriginForEmbeddedAccess;
    this.fullTimeEquivalent = this.globalSettingsService.fullTimeEquivalent;
    this.isChangeExternalIdEnabled = this.globalSettingsService.isChangeExternalIdEnabled;
    this.quickSearchDateRange = this.globalSettingsService.quickSearchDateRange;
    this.skipLdapCertificateValidation = this.globalSettingsService.skipLdapCertificateValidation;

    this.settingsForm = fb.group({
      systemStartDate: null,
      mixedLanguageType: this.mixedLanguageType,
      systemLangCode: null,
      otherLangCodes: [],
      systemTimeZone: null,
      systemDateFormat: null,
      inboundAgentChannel: null,
      outboundAgentChannel: null,
      domainName: null,
      ldapCertificateName: null,
      skipLdapCertificateValidation: null,
      idleTimeoutEnabled: null,
      idleTimeoutMinute: [
        null,
        {
          validators: [Validators.required, IntegerValidator.minMax(1, 30)],
        },
      ],
      userFeedbackReceiverEmail: [
        null,
        {
          validators: [Validators.email],
        },
      ],
      maxAnalysisTime: [
        null,
        {
          validators: [IntegerValidator.minMax(0, 100)],
        },
      ],
      testEmailAddress: [
        null,
        {
          validators: [Validators.email],
        },
      ],
      autoPlayEnabled: null,
      corsOriginForEmbeddedAccess: [
        null,
        {
          validators: [],
        },
      ],
      fullTimeEquivalent: [
        null,
        {
          validators: [IntegerValidator.minMax(0, 999)],
        },
      ],
      isChangeExternalIdEnabled: null,
      quickSearchDateRange: [
        null,
        {
          validators: [IntegerValidator.minMax(0, 365)],
        },
      ],
    });

    this.store
      .select(GenericLookupTypeState.getGenericLookups)
      .pipe(takeUntil(this.autoUnsubscribeNotifier))
      .pipe(map(filterFn => filterFn(CallAgentChannel)))
      .subscribe(result => {
        this.channelTypes = result;
      });

    if (this.otherLangCodes.length > 0) {
      this.settingsForm.get('otherLangCodes').setValue(this.otherLangCodes);
    }

    this.langCodesTemp = [...this.otherLangCodes];
    this.langCodesSettingValue = [...this.otherLangCodes];
    if (this.systemLangCode) {
      this.systemLangCodeTemp = this.systemLangCode;
      this.langCodesSettingValue.push(this.systemLangCode);
      this.langCodesTemp.push(this.systemLangCode);
    }
    let request = {
      filters: [
        {
          field: 'isVisible',
          operator: this.operators.Contains,
          value: true,
        },
      ],
      sorters: [],
      maxResultCount: 99999,
      skipCount: 0,
    };
    this.settingService
      .getLanguageCodeList(request)
      .pipe(take(1))
      .subscribe(res => {
        this.languages = res.items.filter(x => !x.code.includes(';'));
        this.mixedLanguages = res.items.filter(x => x.code.includes(';'));
        this.mixedLanguages.push({
          code: 'Conversation.MixedLanguageType.SystemLanguage',
          id: -1,
          name: 'Conversation.MixedLanguageType.SystemLanguage',
          isVisible: true,
          disabled: false,
          isDeleted: false,
        });

        if (this.languageDetectionFeatureEnabled) {
          this.mixedLanguages.push({
            code: 'Conversation.MixedLanguageType.LanguageDetection',
            id: -2,
            name: 'Conversation.MixedLanguageType.LanguageDetection',
            isVisible: true,
            disabled: false,
            isDeleted: false,
          });
        }
        this.mixedLanguages = this.getMixedLanguageOrder(this.mixedLanguages);
        this.resetOtherLanguagesDataSource();
        if (this.systemLangCode) {
          this.setSelectedOtherLanguagesItem(this.systemLangCode);
        }
      });

    this.settingsForm.get('systemLangCode').valueChanges.subscribe(selectedValue => {
      if (selectedValue) {
        this.setSelectedOtherLanguagesItem(selectedValue);
        if (selectedValue !== this.systemLangCodeTemp) {
          if (this.langCodesSettingValue.filter(x => x === this.systemLangCodeTemp).length === 0) {
            this.langCodesTemp = this.langCodesTemp.filter(x => x !== this.systemLangCodeTemp);
          }
          if (this.langCodesTemp.filter(x => x === selectedValue).length === 0) {
            this.langCodesTemp.push(selectedValue);
          }
          this.systemLangCodeTemp = selectedValue;
          this.resetOtherLanguagesDataSource();
        }
      }
    });

    this.dateFormats = DateFormatConstants.dateFormats;
    // A tenant whose legacy fixed offset (e.g. "+0330") has no DST-free IANA equivalent keeps it
    // as its stored value; offer that one value so the dropdown can still show it as selected.
    this.timeZones = TimeZoneConstants.withFixedOffsetOption(
      TimeZoneConstants.timeZones,
      this.globalSettingsService.systemTimeZone,
      this.localizationService.instant('Settings::Global.SystemTimeZoneFixedOffsetSuffix'),
    );
    this.isSuperAdminUser = this.roleHelper.isSuperAdminUser();
  }

  removeFromSelectedOtherLanguages(languageCode: string) {
    let selectedItems = this.settingsForm.get('otherLangCodes').value;

    if (selectedItems) {
      const indexToRemoveFromSelected = selectedItems.findIndex(i => i === languageCode);
      if (indexToRemoveFromSelected > -1) {
        selectedItems.splice(indexToRemoveFromSelected, 1);
        this.settingsForm.get('otherLangCodes').setValue(selectedItems);
      }
    }
  }

  setSelectedOtherLanguagesItem(languageCode: string) {
    let selectedItems = this.settingsForm.get('otherLangCodes').value ?? [];
    const indexToSelectFromSelected = selectedItems.findIndex(i => i === languageCode);
    if (indexToSelectFromSelected === -1) {
      const indexToSelectFromDataSource = this.otherLanguagesDataSource.findIndex(
        d => d.code === languageCode,
      );
      if (indexToSelectFromDataSource > -1) {
        selectedItems.push(languageCode);
        this.settingsForm.get('otherLangCodes').setValue(selectedItems);
      }
    }
  }

  resetOtherLanguagesDataSource() {
    if (this.languages) {
      this.otherLanguagesDataSource = ObjectHelper.deepCopy(this.languages);
      this.otherLanguagesDataSource.forEach(data => {
        if (this.langCodesTemp.filter(x => x === data.code).length > 0) {
          data.disabled = true;
        }
      });
    }
  }

  isDisabled(code: string) {
    let selectedItems = this.settingsForm.get('otherLangCodes').value;
    if (selectedItems) {
      let result = selectedItems.includes(code);
      if (result) {
        const hasLangCode = this.langCodesTemp.find(p => p === code);
        if (hasLangCode && hasLangCode.length > 0) {
          return true;
        }
      }
      return false;
    }
    return false;
  }

  ngOnInit(): void {}

  onSubmitSettings() {
    if (this.settingsForm.invalid) {
      return;
    }
    this.saveSettings();
  }

  saveSettings() {
    let otherLangCodes = this.settingsForm.get('otherLangCodes').value ?? [];
    otherLangCodes = otherLangCodes.filter(x => x != this.settingsForm.get('systemLangCode').value);
    //TO-DO: there should be more effective solution for the static calculations.
    this.globalSettingDtos = new Array();
    this.globalSettingDtos.push(
      {
        settingName: 'Global.MixedLanguageType',
        settingValue: this.mixedLanguageType.toString(),
      },
      {
        settingName: 'Global.SystemLanguageCode',
        settingValue: this.systemLangCode,
      },
      {
        settingName: 'Global.OtherLanguageCode',
        settingValue: otherLangCodes?.join(';'),
      },
      {
        settingName: 'Global.QueryLanguageCodes',
        settingValue: otherLangCodes?.join(','),
      },
      {
        settingName: 'Global.InboundAgentChannel',
        settingValue: this.inboundAgentChannel.toString(),
      },
      {
        settingName: 'Global.OutboundAgentChannel',
        settingValue: this.outboundAgentChannel.toString(),
      },
      {
        settingName: 'Global.DomainName',
        settingValue: this.domainName.toString(),
      },
      {
        settingName: 'Global.LdapCertificateName',
        settingValue: this.ldapCertificateName.toString(),
      },
      {
        settingName: 'Global.UserFeedbackReceiverEmail',
        settingValue: this.userFeedbackReceiverEmail.toString(),
      },
      {
        settingName: 'Global.MaximumAnalysisTime',
        settingValue: this.maximumAnalysisTime.toString(),
      },
      {
        settingName: 'Global.SystemDateFormat',
        settingValue: this.systemDateFormat.replace(/m/g, 'M'),
      },
      {
        settingName: 'Global.SystemTimeZone',
        settingValue: this.systemTimeZone,
      },
      {
        settingName: 'Global.IdleTimeoutEnabled',
        settingValue: this.idleTimeoutEnabled.toString(),
      },
      {
        settingName: 'Global.IdleTimeoutMinute',
        settingValue: this.idleTimeoutMinute.toString(),
      },
      {
        settingName: 'Global.AutoPlayEnabled',
        settingValue: this.autoPlayEnabled.toString(),
      },
      {
        settingName: 'Auth.CorsOriginForEmbeddedAccess',
        settingValue: this.corsOriginForEmbeddedAccess,
      },
      {
        settingName: 'Global.FullTimeEquivalent',
        settingValue: this.fullTimeEquivalent.toString(),
      },
      {
        settingName: 'User.IsChangeExternalIdEnabled',
        settingValue: this.isChangeExternalIdEnabled.toString(),
      },
      {
        settingName: 'Global.QuickSearchDateRange',
        settingValue: this.quickSearchDateRange.toString(),
      },
      {
        settingName: 'Global.SkipLdapCertificateValidation',
        settingValue: this.skipLdapCertificateValidation.toString(),
      },
    );

    this.settingService
      .saveSetting(this.globalSettingDtos)
      .pipe(take(1))
      .subscribe({
        next: res => {
          let oldLanguageCodes = [...this.langCodesSettingValue];
          let newLangaugeCodes = [...this.settingsForm.get('otherLangCodes').value];
          let difference = newLangaugeCodes.filter(x => !oldLanguageCodes.includes(x));
          if (difference && difference.length > 0) {
            this.globalSettingsService
              .generatePredefinedItems(difference.join(';'))
              .pipe(take(1))
              .subscribe({
                next: res => {
                  if (res != null && res.length > 0) {
                    this.toastr.warn(res);
                  } else {
                    this.toastr.success(
                      this.localizationService.instant(
                        'Settings::Global.SuccessfullyGeneratePredefinedForSecondaryLang',
                      ),
                    );
                  }
                },
                error: error => {
                  this.toastr.warn(error.error.text);
                },
              });
          }

          this.langCodesSettingValue = [...this.settingsForm.get('otherLangCodes').value];
          this.langCodesTemp = [...this.settingsForm.get('otherLangCodes').value];
          this.resetOtherLanguagesDataSource();
          this.toastr.success(
            this.localizationService.instant('AbpSettingManagement::SavedSuccessfully'),
          );
          this.refreshSystemTimeZoneLockState();
        },
        error: () => {
          this.refreshSystemTimeZoneLockState();
        },
      });
  }

  // Admin-role users (host or CA-seeded tenant admin) and impersonating host admins are CA
  // support/ops and may change a locked system time zone.
  private canBypassSystemTimeZoneLock(): boolean {
    return this.roleHelper.isSuperAdminUser() || this.roleHelper.isHostAdmin();
  }

  private refreshSystemTimeZoneLockState(): void {
    this.config
      .refreshAppState()
      .pipe(take(1))
      .subscribe(() => {
        this.isSystemTimeZoneLocked =
          this.globalSettingsService.isSystemTimeZoneConfirmed &&
          this.globalSettingsService.hasEverHadConversationData &&
          !this.canBypassSystemTimeZoneLock();
        this.isSystemTimeZonePendingConfirmation =
          !this.globalSettingsService.isSystemTimeZoneConfirmed;
      });
  }

  sendTestEmail() {
    const isValid = this.settingsForm.get('testEmailAddress').valid;
    if (!isValid) {
      return;
    }

    this.globalSettingsService
      .sendTestMail(this.testEmailAddress)
      .pipe(take(1))
      .subscribe(res => {
        if (res) {
          this.toastr.success(
            this.localizationService.instant('Settings::Global.SuccessfullySendTestEmail'),
          );
        }
      });
  }

  getMixedLanguageOrder(items: CallLanguageCodeDto[]) {
    const orderCodes = [
      'Conversation.MixedLanguageType.SystemLanguage',
      'Conversation.MixedLanguageType.SixLanguagesMulti',
      'Conversation.MixedLanguageType.FourLanguagesMulti',
      'Conversation.MixedLanguageType.EnglishArabic',
      'Conversation.MixedLanguageType.DutchFrench',
      this.languageDetectionFeatureEnabled
        ? 'Conversation.MixedLanguageType.LanguageDetection'
        : null,
      'Conversation.MixedLanguageType.MixedModel4',
      'Conversation.MixedLanguageType.MixedModel5',
      'Conversation.MixedLanguageType.MixedModel6',
      'Conversation.MixedLanguageType.MENA',
      'Conversation.MixedLanguageType.NorthAmerica',
      'Conversation.MixedLanguageType.MixedModel7',
      'Conversation.MixedLanguageType.MixedModel8',
      'Conversation.MixedLanguageType.MixedModel9',
      'Conversation.MixedLanguageType.MixedModel10',
      'Conversation.MixedLanguageType.MixedModel11',
      'Conversation.MixedLanguageType.MixedModel12',
      'Conversation.MixedLanguageType.MixedModel13',
    ].filter(Boolean);

    let orderedList = orderCodes
      .map(name => items.find(i => i.name === name))
      .filter((x): x is CallLanguageCodeDto => !!x);

    orderedList.push(...items.filter(i => !orderCodes.includes(i.name)));

    return orderedList;
  }
}
